import type { KeyboardEvent } from 'react';
import type { LucideIcon } from 'lucide-react';
import { SwitchVisual } from '~/components/product/switch-visual';
import { cn } from '@/lib/utils';

interface ToggleRowProps {
	label: string;
	help?: string;
	icon?: LucideIcon;
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
	disabled?: boolean;
}

/**
 * Large clickable row with label + help + switch. The whole row is the control
 * (role="switch"); the inner `SwitchVisual` is purely presentational — NOT a Radix
 * Switch, which (when nested in a clickable row) re-dispatches the bubbled click and
 * causes a setValue↔re-render infinite loop. See switch-visual.tsx.
 */
export function ToggleRow({ label, help, icon: Icon, checked, onCheckedChange, disabled }: ToggleRowProps) {
	const toggle = () => {
		if (!disabled) onCheckedChange(!checked);
	};
	const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === ' ' || e.key === 'Enter') {
			e.preventDefault();
			toggle();
		}
	};
	return (
		<div
			role="switch"
			aria-checked={checked}
			aria-disabled={disabled}
			tabIndex={disabled ? -1 : 0}
			onClick={toggle}
			onKeyDown={onKeyDown}
			className={cn(
				'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors',
				disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
				checked ? 'border-brand-border-subtle bg-brand-subtle' : 'border-border bg-surface'
			)}>
			{Icon && <Icon size={18} className="shrink-0 text-foreground-muted" />}
			<span className="flex-1">
				<span className="block text-sm font-medium text-foreground">{label}</span>
				{help && <span className="mt-0.5 block text-xs text-foreground-muted">{help}</span>}
			</span>
			<SwitchVisual checked={checked} disabled={disabled} />
		</div>
	);
}
